/* =============================================================
   PIXEL CURSORS -- the arrow, the pointing hand and the text beam,
   drawn here.

   Three pixel-art cursors, drawn pixel by pixel below on a small grid
   and scaled up one and a half times with crisp edges, so they stay square
   pixels at any screen density. White, edged in the theme:

     o  outline    --pg-accent   (the reader's palette)
     f  fill       white
     h  highlight  white

   A cursor image cannot read CSS variables, so this builds the two
   images as SVG data URLs from the colours in force and writes them to
   :root as --cursor-arrow, --cursor-pointer and --cursor-text, which
   css/theme/cursors.css applies. They are built again whenever the
   theme changes -- ThemeContext writes the palette onto <html> as
   inline custom properties and the mode as data-mode, and a watch on
   those two is all it takes.

   Pointers: cursors.css gives the hand to links, buttons and the
   like by selector. A component that only sets `cursor: pointer` in
   its own CSS (a clickable row, a menu option) is found as the mouse
   reaches it -- its computed cursor is "pointer" -- and marked, so it
   gets the hand too.
   ============================================================= */

// Each grid cell is this many CSS pixels: 1.5 keeps the cursors near a
// system cursor's size (the arrow 18x24, the hand 24x24).
const SCALE = 1.5;

// Rows of the grid: o outline, f fill, h highlight, . clear.
const ARROW = [
  'o...........',
  'oo..........',
  'oho.........',
  'ohfo........',
  'ohffo.......',
  'ohfffo......',
  'ohffffo.....',
  'ohfffffo....',
  'ohffffffo...',
  'ohfffffffo..',
  'ohffffoooo..',
  'ohfoffo.....',
  'ofo.offo....',
  'oo..offo....',
  '.....offo...',
  '......oo....',
];

const HAND = [
  '....oo..........',
  '...ohfo.........',
  '...ohfo.........',
  '...ohfo.........',
  '...offoooo......',
  '...offoffoooo...',
  '...offoffoffooo.',
  'oo.offoffoffofo.',
  'ohooffffffffffo.',
  'ohfoffffffffffo.',
  '.offffffffffffo.',
  '..offfffffffffo.',
  '..offfffffffffo.',
  '...offffffffffo.',
  '....offffffffo..',
  '....oooooooooo..',
];

// The text cursor: an I-beam with its serifs, for typing.
const BEAM = [
  'ooo.ooo',
  'offoffo',
  'ooofooo',
  '..ofo..',
  '..ofo..',
  '..ofo..',
  '..ofo..',
  '..ofo..',
  '..ofo..',
  '..ofo..',
  '..ofo..',
  '..ofo..',
  '..ofo..',
  'ooofooo',
  'offoffo',
  'ooo.ooo',
];

// The pixel that clicks, in grid cells: the arrow's tip, the fingertip,
// the middle of the beam.
const ARROW_HOT = [0, 0];
const HAND_HOT = [4.5, 0];
const BEAM_HOT = [3, 7.5];

/* ── Colours ───────────────────────────────────────────────── */

// Any CSS colour as [r, g, b], read back from the browser.
let probe = null;
const rgbOf = (value) => {
  if (!probe) {
    probe = document.createElement('span');
    probe.style.display = 'none';
    document.body.appendChild(probe);
  }
  probe.style.color = '';
  probe.style.color = value;
  const m = /rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(getComputedStyle(probe).color);
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
};

const hex = ([r, g, b]) => `#${[r, g, b].map((n) => Math.round(n).toString(16).padStart(2, '0')).join('')}`;
// White body; only the edge is the theme's accent.
const coloursNow = () => {
  const root = getComputedStyle(document.documentElement);
  const accent = rgbOf(root.getPropertyValue('--pg-accent').trim() || '#3B6FF0') || [59, 111, 240];
  return { o: hex(accent), f: '#ffffff', h: '#ffffff' };
};

/* ── Drawing ───────────────────────────────────────────────── */

// The grid as an SVG: one square per pixel, merged along each row.
const svgOf = (grid, colours) => {
  // Drawn in grid cells and shown at SCALE: the browser scales the
  // squares up, crispEdges keeps them squares.
  const cols = grid[0].length;
  const rows = grid.length;
  const rects = [];
  grid.forEach((row, y) => {
    let x = 0;
    while (x < row.length) {
      const ch = row[x];
      let end = x + 1;
      while (end < row.length && row[end] === ch) end += 1;
      if (colours[ch]) {
        rects.push(`<rect x="${x}" y="${y}" width="${end - x}" height="1" fill="${colours[ch]}"/>`);
      }
      x = end;
    }
  });
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${cols * SCALE}" height="${rows * SCALE}" viewBox="0 0 ${cols} ${rows}" shape-rendering="crispEdges">${rects.join('')}</svg>`;
};

const cursorOf = (grid, [hx, hy], colours, fallback) =>
  `url("data:image/svg+xml,${encodeURIComponent(svgOf(grid, colours))}") ${Math.round((hx + 0.5) * SCALE)} ${Math.round((hy + 0.5) * SCALE)}, ${fallback}`;

let lastKey = '';

// Draw both for the colours in force; nothing is written when they
// have not changed -- writing to :root is itself what the watch sees.
const paint = () => {
  const colours = coloursNow();
  const key = colours.o;
  if (key === lastKey) return;
  lastKey = key;
  const root = document.documentElement.style;
  root.setProperty('--cursor-arrow', cursorOf(ARROW, ARROW_HOT, colours, 'default'));
  root.setProperty('--cursor-pointer', cursorOf(HAND, HAND_HOT, colours, 'pointer'));
  root.setProperty('--cursor-text', cursorOf(BEAM, BEAM_HOT, colours, 'text'));
};

/* ── Marking what only its own CSS makes clickable ─────────── */

const markPointer = (event) => {
  const el = event.target;
  if (!(el instanceof Element) || el.hasAttribute('data-pc-pointer')) return;
  if (getComputedStyle(el).cursor === 'pointer') el.setAttribute('data-pc-pointer', '');
};

let started = false;

export const startPixelCursors = () => {
  if (started || typeof document === 'undefined') return;
  started = true;

  const begin = () => {
    paint();
    // The theme is painted onto <html>: its inline style (the palette)
    // and data-mode (day or night). Redraw, once per frame at most.
    let queued = false;
    new MutationObserver(() => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        paint();
      });
    }).observe(document.documentElement, { attributes: true, attributeFilter: ['style', 'data-mode'] });
    document.addEventListener('pointerover', markPointer, { passive: true });
  };

  if (document.body) begin();
  else document.addEventListener('DOMContentLoaded', begin, { once: true });
};

export default startPixelCursors;
